/**
 * Phone presentation + invite-entry helpers.
 *
 * Normalization has exactly one source of truth: `normalizePhoneE164` in
 * `@/lib/inviteRecipients`. Everything here re-uses it.
 */
import {
  normalizePhoneE164,
  parseInviteToken,
  type InviteRecipient,
} from "@/lib/inviteRecipients";

export { normalizePhoneE164 };

/** Seconds a user must wait before another verification code can be sent. */
export const RESEND_COOLDOWN_SECONDS = 60;

/** Minutes a verification code stays valid. */
export const OTP_EXPIRY_MINUTES = 10;

export type PhoneStatus = "not_added" | "not_verified" | "verified";

export function getPhoneStatus(
  phone: string | null | undefined,
  verified: boolean | null | undefined,
): PhoneStatus {
  if (!phone || !phone.trim()) return "not_added";
  return verified ? "verified" : "not_verified";
}

export const PHONE_STATUS_LABEL: Record<PhoneStatus, string> = {
  not_added: "Not added",
  not_verified: "Not verified",
  verified: "Verified",
};

/**
 * Mask a phone for display: keep only the last 4 digits.
 * `+15551234567` -> `+1 ••• ••• 4567`
 */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const normalized = normalizePhoneE164(phone) ?? phone;
  const digits = normalized.replace(/\D/g, "");
  if (digits.length < 4) return "•••";
  return `••• ••• ${digits.slice(-4)}`;
}

export type InviteEntryStatus = "valid" | "duplicate" | "invalid";

export type ClassifiedInviteEntry = {
  raw: string;
  status: InviteEntryStatus;
  kind: "email" | "phone" | null;
  /** Canonical value (lowercased email / E.164 phone) when valid. */
  value: string | null;
  reason?: string;
};

/**
 * Classify a list of raw invite entries so the UI can show what is valid,
 * what is a duplicate of an earlier entry, and what cannot be sent —
 * BEFORE anything is sent.
 */
export function classifyInviteEntries(values: string[]): ClassifiedInviteEntry[] {
  const seen = new Set<string>();
  return values
    .map((v) => v.trim())
    .filter(Boolean)
    .map<ClassifiedInviteEntry>((raw) => {
      const parsed: InviteRecipient = parseInviteToken(raw);
      if (parsed.kind === "invalid") {
        return { raw, status: "invalid", kind: null, value: null, reason: parsed.reason };
      }
      const value = parsed.kind === "email" ? parsed.email : parsed.phone;
      const key = `${parsed.kind}:${value}`;
      if (seen.has(key)) {
        return { raw, status: "duplicate", kind: parsed.kind, value };
      }
      seen.add(key);
      return { raw, status: "valid", kind: parsed.kind, value };
    });
}

/** Split a pasted blob into individual entries. */
export function splitInviteInput(input: string): string[] {
  return input
    .split(/[,\n;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Feature-detect the Contact Picker API. Never assume support — most
 * browsers (all desktop, iOS Safari) do not implement it.
 */
export function isContactPickerSupported(
  nav: unknown = typeof navigator !== "undefined" ? navigator : undefined,
  win: unknown = typeof window !== "undefined" ? window : undefined,
): boolean {
  if (!nav || !win) return false;
  const contacts = (nav as { contacts?: { select?: unknown } }).contacts;
  const hasManager = "ContactsManager" in (win as object);
  return Boolean(contacts && typeof contacts.select === "function" && hasManager);
}

/**
 * Shown next to "Send code". Transactional ONLY — requesting a one-time
 * verification/login text must never imply consent to plan invites,
 * reminders, bail alerts, marketing, or any recurring SMS.
 */
export const OTP_TRANSACTIONAL_NOTICE =
  "Tapping Send code requests a one-time verification text. Message and data rates may apply. This does not sign you up for recurring texts.";

/** Shown once near phone entry — keep it short, no legal walls of text. */
export const PHONE_INVITE_NOTICE =
  "Mobile numbers get a one-time text invite. Standard message rates may apply.";
